import { reportBelongsToSite } from '../../../../lib/reportSiteIdentity';
/**
 * PathToGoReportSection (pathToGo) — the LIVE Path-to-GO steps, rendered
 * READ-ONLY inside the Executive Summary report. Recomputed at render from the
 * site's CURRENT reports / intent / evidence via `buildPathToGoState` — the SAME
 * deterministic engine the site page's Decide surface and Home journey band
 * read (the interactive Path to GO wizard was deleted in Path to GO removal
 * stage 3) — so the report's "what's left to reach GO" list can never drift
 * from them. NO generate step, NO LLM.
 *
 * READ-ONLY: this surface shows the ranked steps + blockers + required-report
 * gate; it does NOT expose the wizard's interactive affordances (upload / edit
 * intent / launch report). It is a printed snapshot of the live path.
 *
 * W2c editorial port — the hand-rolled mono uppercase group labels move onto
 * the editorial primitives, mapped from the data's REAL semantics:
 *   - required-report gate → EditorialSubsection ledger (a present/missing
 *     checklist, not a sequence);
 *   - blockers → EditorialSubsection findings (severity-toned, not actions);
 *   - ranked next steps → EditorialRecommendations (a value-RANKED numbered
 *     action list — NOT EditorialTimeline: the steps carry no dates / phases /
 *     owners and none are fabricated; the severity-toned glyph is retained).
 *
 * THEME-AWARE via roleColor() (same pattern as the Financial renderers); the
 * page stays white via the report's `data-theme-scope='light'` island. No
 * bespoke hardcoded colors — severity tone maps through negative / warning /
 * accent.
 *
 * LIVE DATA-FLOW: the router passes only `report` + `label`, so this reads its
 * inputs from the store via `useStore`, honoring the Zustand rule (subscribe to
 * roots, derive per-site slices in useMemo).
 */
import React from 'react';
import { Check, Circle } from 'lucide-react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialCallout, EditorialRecommendations, EditorialSubsection } from '../../report/editorial';
import {
  decisionSnapshotStaleness,
  describeDecisionSnapshotStaleness,
} from '../../../../lib/reportGeneration/decisionSnapshotStaleness';
import { useStore } from '../../../../store';
import { REPORT_TYPE_REGISTRY } from '../../../../config/reportTypes';
import {
  buildPathToGoState,
  type PathToGoState,
} from '../../../../lib/pathToGo/pathToGoOrchestrator';
import type { NextStepSeverity } from '../../../../lib/pathToGo/nextStep';

const SEVERITY_ROLE: Record<NextStepSeverity, 'negative' | 'warning' | 'accent'> = {
  critical: 'negative',
  significant: 'warning',
  noted: 'accent',
};

export const PathToGoReportSection: React.FC<{
  report: SiteRagReport;
  label: string;
}> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();

  const allReports = useStore((s) => s.siteRag.reports);
  const sites = useStore((s) => s.siteRag.sites);
  const composites = useStore((s) => s.portfolio?.composites);
  const bandConfig = useStore((s) => s.portfolio?.bandConfig ?? null);

  const site = React.useMemo(
    () => (report.siteId ? sites.find((x) => x.id === report.siteId) ?? null : null),
    [sites, report.siteId],
  );

  const siteReports = React.useMemo<SiteRagReport[]>(() => {
    if (!site) return [];
    return allReports.filter(
      (r) =>
        reportBelongsToSite(site, r),
    );
  }, [allReports, site]);

  const composite = React.useMemo(
    () => (site ? composites?.[site.id] ?? null : null),
    [composites, site],
  );

  const state = React.useMemo<PathToGoState | null>(() => {
    // Report P0 — prefer the gen-time snapshot (the print/export subtree has a COLD store; a live
    // recompute would blank this primary band in the PDF). Live recompute is the legacy fallback for
    // reports stamped before the snapshot field existed.
    if (report.pathToGoSnapshotAtGen?.state) return report.pathToGoSnapshotAtGen.state;
    if (!site) return null;
    return buildPathToGoState(
      site,
      null,
      siteReports,
      site.projectIntent,
      bandConfig,
      composite,
    );
  }, [report, site, siteReports, bandConfig, composite]);

  if (!state) {
    return <SectionShell label={label} hasData={false} report={report} />;
  }

  // present/missing for the required-diligence checklist — prefer the snapshot's gen-time present set;
  // the live siteReports are empty in the print subtree (which would mark every required report
  // "Outstanding" in the PDF).
  const present = new Set<string>(
    report.pathToGoSnapshotAtGen?.presentReportTypes ??
      siteReports.map((r) => r.reportType ?? 'feasibility'),
  );
  const topSteps = state.rankedSteps.slice(0, 8);
  // P1.4b — the frozen snapshot is preferred (cold-store print subtree), but a
  // snapshot the site's evidence has moved past says so. Empty in the print
  // subtree (no siblings) ⇒ no note, never a fabricated one.
  const frozenStaleNote = report.pathToGoSnapshotAtGen?.state
    ? describeDecisionSnapshotStaleness(report, decisionSnapshotStaleness(report, siteReports))
    : null;
  const hasData =
    state.requiredReports.length > 0 ||
    state.blockers.length > 0 ||
    topSteps.length > 0;

  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      <div className="space-y-4">
        {frozenStaleNote && (
          <div data-testid="exec-pathtogo-stale">
            <EditorialCallout theme={theme} tone="warning" label="Decision basis has moved">
              {frozenStaleNote}
            </EditorialCallout>
          </div>
        )}
        {/* Required-report gate — the GO diligence set + present/missing state. */}
        {state.requiredReports.length > 0 ? (
          <EditorialSubsection theme={theme} label="Required diligence for GO">
            <ul className="space-y-1" data-testid="exec-pathtogo-required">
              {state.requiredReports.map((t) => {
                const done = present.has(t);
                const ink = done
                  ? roleColor(theme, 'positive')
                  : roleColor(theme, 'warning');
                const typeLabel = REPORT_TYPE_REGISTRY[t]?.shortLabel ?? t;
                return (
                  <li
                    key={`req-${t}`}
                    className="text-sm flex items-baseline gap-2"
                    style={{ color: roleColor(theme, 'body') }}
                  >
                    <span
                      aria-hidden="true"
                      className="inline-flex h-[1em] w-[1em] shrink-0 items-center justify-center"
                      style={{ color: ink }}
                    >
                      {done ? (
                        <Check
                          data-pdf-diligence-status-mark="generated"
                          className="h-[0.9em] w-[0.9em]"
                          strokeWidth={2}
                        />
                      ) : (
                        <Circle
                          data-pdf-diligence-status-mark="outstanding"
                          className="h-[0.75em] w-[0.75em]"
                          strokeWidth={1.75}
                        />
                      )}
                    </span>
                    <span className="font-medium">{typeLabel}</span>
                    <span className="grow" />
                    <span style={{ color: ink }}>
                      {done ? 'Generated' : 'Outstanding'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </EditorialSubsection>
        ) : null}

        {/* Blockers — what is holding the site back from GO. */}
        {state.blockers.length > 0 ? (
          <EditorialSubsection theme={theme} label="Blockers">
            <ul className="space-y-2" data-testid="exec-pathtogo-blockers">
              {state.blockers.map((b) => (
                <li
                  key={b.id}
                  className="text-sm"
                  style={{ color: roleColor(theme, 'body') }}
                >
                  <div className="flex gap-2">
                    <span aria-hidden style={{ color: roleColor(theme, SEVERITY_ROLE[b.severity]) }}>
                      {'•'}
                    </span>
                    <span className="font-medium">{b.title}</span>
                  </div>
                  {b.why ? (
                    <div className="pl-4 text-[13px]" style={{ color: roleColor(theme, 'muted') }}>
                      {b.why}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </EditorialSubsection>
        ) : null}

        {/* Ranked next steps — value-RANKED actions toward GO, so the numbered
            EditorialRecommendations list is the honest treatment. GAP: the
            steps carry no dates / phase windows / owners — none are fabricated
            (no EditorialTimeline). The severity-toned glyph is retained inside
            each item so the severity signal survives the port unchanged. */}
        {topSteps.length > 0 ? (
          <div data-testid="exec-pathtogo-steps">
            <EditorialRecommendations
              theme={theme}
              heading="Highest-value next steps"
              marker={theme.headingScale === 'compact' ? 'decimal' : 'roman'}
              items={topSteps.map((step) => (
                <span key={step.id}>
                  <span
                    aria-hidden
                    style={{ color: roleColor(theme, SEVERITY_ROLE[step.severity]) }}
                  >
                    {'– '}
                  </span>
                  <span className="font-medium">{step.label}</span>
                  {step.why ? (
                    <span
                      className="block pl-4 text-[13px]"
                      style={{ color: roleColor(theme, 'muted') }}
                    >
                      {step.why}
                    </span>
                  ) : null}
                </span>
              ))}
            />
          </div>
        ) : null}
      </div>
    </SectionShell>
  );
};
